import {
  Folder,
  Search,
  FolderPlus,
  Plus,
  MoreHorizontal,
  ChevronRight,
  ChevronDown,
  X,
  RefreshCw,
  Upload,
} from 'lucide-react';
import CollectionSelectionModal from '../CollectionSelectionModal';
import OsScroll from '../ui/OsScroll';
import { ConfirmModal } from '../ui/Modal';
import SchemaSidebar from '../SchemaSidebar';
import LocalCapHint from '../plans/LocalCapHint';
import { CollectionBlock } from './CollectionBlock';

/** The sidebar markup (list section + tree). Plain function called during render, so the element tree is unchanged. */
export function renderSidebar({
  t,
  collections,
  onRefreshCollections,
  isLocalWorkspace,
  workspaceId,
  onCreateCollection,
  onCreateRequest,
  onCreateFolder,
  onDeleteFolder,
  onSelectRequest,
  onSelectCollection,
  onSelectFolder,
  onDeleteRequest,
  onDeleteCollection,
  onCreateFolderReturningId,
  selectedRequest,
  selectedCollection,
  selectedFolder,
  editingItem,
  onSaveRename,
  onRenameDraft,
  onCancelRename,
  schemas,
  activeSchemaId,
  onOpenSchema,
  onCreateSchema,
  onCreateSchemaFolder,
  onRenameSchemaEntry,
  onDeleteSchemaEntry,
  onDuplicateSchema,
  onMoveSchemaEntry,
  editingSchemaItem,
  onStartSchemaRename,
  onSaveSchemaRename,
  onCancelSchemaRename,
  selectedSchemaEntry,
  onSelectSchemaEntry,
  fadeInIds,
  collectionsCollapsed,
  expandedCollections,
  expandedFolders,
  contextMenu,
  bgMenu,
  deleteConfirm,
  setDeleteConfirm,
  movePickerOpen,
  setMovePickerOpen,
  searchTerm,
  setSearchTerm,
  activeDragId,
  dropIndicator,
  fileDragActive,
  handleFileDragEnter,
  handleFileDragOver,
  handleFileDragLeave,
  handleFileDrop,
  toggleCollection,
  toggleCollectionsSection,
  filteredCollections,
  markedIds,
  dndDisabled,
  renderCollections,
  selection,
  handleRowClick,
  hasMultiSelection,
  dragX,
  dragY,
  overlayRef,
  getHandleProps,
  toggleFolder,
  handleContextMenu,
  handleBgContextMenu,
  closeBgMenu,
  handleBgOverlayDismiss,
  clearSearch,
  overlayLabel,
  handleBulkDeleteConfirm,
  handleBulkMove,
  handleTreeKeyDownCapture,
  renderContextMenu,
}) {
  // ── Render ─────────────────────────────────────────────────────────────────

  const listSection = (
    <>
      {(searchTerm ? filteredCollections : collections).length === 0 ? (
        searchTerm ? (
          <div className="sidebar-no-results">
            <Search size={22} className="sidebar-no-results__icon" aria-hidden />
            <p className="sidebar-no-results__title">{t('sidebar.noResultsFound')}</p>
            <p className="sidebar-no-results__hint">{t('sidebar.adjustSearch')}</p>
            {/* A real class, not `text-blue-600 … px-4 py-2`: a <button> with no
                declared background keeps the browser's, which is light in both
                themes — so this sat in dark mode as a white pill (owner,
                23.09.2026). It was also sized like a form button in a 240px
                sidebar. */}
            <button type="button" className="sidebar-no-results__clear" onClick={clearSearch}>
              {t('sidebar.clearSearch')}
            </button>
          </div>
        ) : (
          <div className="py-8 text-center">
            <div className="sidebar-empty-icon-wrap">
              <div className="sidebar-empty-icon">
                <Folder size={16} />
              </div>
            </div>
            <p className="mb-1 text-xs font-medium text-gray-600 dark:text-zinc-200">{t('sidebar.noCollectionsYet')}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-400">{t('sidebar.organizeHint')}</p>
          </div>
        )
      ) : (
        <div
          className="space-y-1"
          role="tree"
          aria-multiselectable="true"
          onKeyDownCapture={handleTreeKeyDownCapture}
        >
          {renderCollections.map((collection) => (
            <div key={collection.id} className="animate-fadeIn">
              <CollectionBlock
                onRequestDelete={(type, item) => setDeleteConfirm({ type, item })}
                collection={collection}
                isMarked={markedIds.has(`collection-${collection.id}`)}
                markedIds={markedIds}
                onRowClick={handleRowClick}
                expanded={expandedCollections.has(collection.id)}
                onToggle={toggleCollection}
                onCreateRequest={onCreateRequest}
                onContextMenu={handleContextMenu}
                disableDnd={dndDisabled}
                activeDragId={activeDragId}
                selectedRequest={selectedRequest}
                selectedCollection={selectedCollection}
                selectedFolder={selectedFolder}
                onSelectRequest={onSelectRequest}
                onSelectCollection={onSelectCollection}
                onSelectFolder={onSelectFolder}
                editingItem={editingItem}
                onSaveRename={onSaveRename}
                onRenameDraft={onRenameDraft}
                onCancelRename={onCancelRename}
                isLocalWorkspace={isLocalWorkspace}
                expandedFolders={expandedFolders}
                onToggleFolder={toggleFolder}
                onCreateFolder={onCreateFolder}
                dropIndicator={dropIndicator}
                getHandleProps={getHandleProps}
                fadeInIds={fadeInIds}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      <div
        className="relative w-full h-full bg-gray-50 flex flex-col dark:bg-zinc-900"
        onDragEnter={handleFileDragEnter}
        onDragOver={handleFileDragOver}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
      >
        {fileDragActive && (
          <div className="sidebar-file-drop-overlay" data-testid="sidebar-file-drop-overlay">
            <Upload size={22} aria-hidden />
            <span>{t('sidebar.dropToImport')}</span>
          </div>
        )}
        {/* Header */}
        <div className="flex-shrink-0 px-2 pt-2 border-b border-gray-200">
          <div className="sidebar-panel-search-row">
            <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
            <input
              type="text"
              placeholder={t('sidebar.filterCollections')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
              autoComplete="off"
            />
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
              {searchTerm ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                  aria-label={t('sidebar.clearSearch')}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <X size={14} strokeWidth={2} aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Scrollable list */}
        <OsScroll
          className="flex-1 min-h-0 px-2 pb-3"
          onContextMenu={handleBgContextMenu}
        >
          {/* Collections section */}
          <div className="pt-1">
            <div className="flex items-center justify-between pb-1 group/section">
              <button
                type="button"
                onClick={toggleCollectionsSection}
                style={{ marginLeft: '-6px' }}
                className="flex items-center gap-2 flex-1 min-w-0 bg-transparent p-0 cursor-pointer"
              >
                {collectionsCollapsed ? (
                  <ChevronRight size={14} className="text-gray-400 shrink-0" aria-hidden />
                ) : (
                  <ChevronDown size={14} className="text-gray-400 shrink-0" aria-hidden />
                )}
                <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide dark:text-zinc-400">
                  {t('sidebar.collections')}
                </h2>
              </button>
              <div className="flex items-center space-x-1">
                {/* Visible AND announced: a user about to confirm "delete 14"
                    should be able to see the 14 before they do. role=status
                    makes the same number reach a screen reader. */}
                {hasMultiSelection && (
                  <span
                    role="status"
                    aria-live="polite"
                    data-testid="sidebar-selection-count"
                    className="text-xs font-medium text-gray-500 mr-1 shrink-0"
                  >
                    {t('sidebar.selectionCount', { count: markedIds.size })}
                  </span>
                )}
                {onRefreshCollections && (
                  <button
                    type="button"
                    onClick={onRefreshCollections}
                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded bg-transparent transition-colors duration-150"
                    title={t('sidebar.refreshCollections')}
                  >
                    <RefreshCw size={14} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onCreateCollection}
                  className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded bg-transparent transition-colors duration-150"
                  title={t('sidebar.newCollection')}
                >
                  <Plus size={14} />
                </button>
                {/* Plans v2 card 3.3: local workspaces only (a cloud one is not capped here). */}
                {isLocalWorkspace && <LocalCapHint kind="collections" />}
                {isLocalWorkspace && <LocalCapHint kind="requests" />}
                {/* "…" replaced the standalone Import button (owner's call,
                    07.08.2026) and holds New collection / Import / Export.
                    The "+" STAYS: creating a collection is the most-used
                    action here, and burying it one menu deep to save a 14px
                    icon would trade a click for nothing. Export lives only
                    here — it never had a header button at all, only the
                    per-collection right-click. */}
                <button
                  type="button"
                  onClick={(e) => handleContextMenu(e, 'collections-header', null)}
                  className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded bg-transparent transition-colors duration-150"
                  title={t('sidebar.collectionsMore', 'More…')}
                >
                  <MoreHorizontal size={14} />
                </button>
              </div>
            </div>
            {!collectionsCollapsed && listSection}
          </div>

          {/* Schemas section */}
          <SchemaSidebar
            embedded
            searchTerm={searchTerm}
            schemas={schemas}
            currentWorkspaceId={workspaceId}
            activeSchemaId={activeSchemaId}
            onOpenSchema={onOpenSchema}
            onCreateSchema={onCreateSchema}
            onCreateSchemaFolder={onCreateSchemaFolder}
            onRenameSchemaEntry={onRenameSchemaEntry}
            onDeleteSchemaEntry={onDeleteSchemaEntry}
            onDuplicateSchema={onDuplicateSchema}
            onMoveSchemaEntry={onMoveSchemaEntry}
            editingItem={editingSchemaItem}
            onStartRename={onStartSchemaRename}
            onSaveRename={onSaveSchemaRename}
            onCancelRename={onCancelSchemaRename}
            selectedEntry={selectedSchemaEntry}
            onSelectEntry={onSelectSchemaEntry}
          />
        </OsScroll>
      </div>

      {/* Drag overlay chip — follows cursor, pointer-events:none */}
      {activeDragId && overlayLabel && (
        <div
          ref={overlayRef}
          // A stable hook for the e2e specs: a drag is only really in flight
          // once this exists, so the tests wait for it instead of guessing at
          // timing. Guessing made the drag regression guard flaky, and a flaky
          // guard on a path that has regressed before is worse than none.
          data-drag-overlay="true"
          style={{
            position: 'fixed',
            left: dragX,
            top: dragY,
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {overlayLabel.type === 'collection' && (
            <div className="flex w-[min(100%,260px)] items-center gap-1 rounded-md sidebar-tree-row-active border px-2 py-1 text-xs font-medium text-gray-900 shadow-lg dark:text-zinc-200">
              <ChevronRight size={14} className="shrink-0 text-gray-600" aria-hidden />
              <Folder size={16} className="shrink-0 text-amber-600" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{overlayLabel.name}</span>
            </div>
          )}
          {overlayLabel.type === 'folder' && (
            <div className="flex w-[min(100%,260px)] items-center gap-1 rounded-md sidebar-tree-row-active border px-2 py-1 text-xs font-medium text-gray-900 shadow-lg dark:text-zinc-200">
              <Folder size={14} className="shrink-0 text-amber-600" aria-hidden />
              <span className="min-w-0 flex-1 truncate">{overlayLabel.name}</span>
            </div>
          )}
          {overlayLabel.type === 'request' && (
            <div className="flex items-center rounded-md sidebar-tree-row-active border px-3 py-2 text-xs shadow-md dark:text-zinc-200">
              <span
                className={`sidebar-request-method mr-1 shrink-0 font-semibold uppercase ${overlayLabel.badge.className}`}
              >
                {overlayLabel.badge.label}
              </span>
              <span className="truncate text-gray-900">{overlayLabel.name}</span>
            </div>
          )}
        </div>
      )}

      {/* Context menu */}
      {contextMenu && renderContextMenu(contextMenu)}

      {/* Background right-click menu */}
      {bgMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleBgOverlayDismiss} />
          <div
            className="fixed z-50 min-w-44 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
            style={{ left: bgMenu.x, top: bgMenu.y }}
          >
            {onCreateCollection && (
              <button
                type="button"
                onClick={() => { onCreateCollection(); closeBgMenu(); }}
                className="ctx-menu-btn"
              >
                <Plus size={14} />
                <span>{t('sidebar.newCollection')}</span>
              </button>
            )}
            {onCreateFolder && selectedCollection && (
              <button
                type="button"
                onClick={() => { onCreateFolder(selectedCollection.id, selectedFolder?.id ?? null); closeBgMenu(); }}
                className="ctx-menu-btn"
              >
                <FolderPlus size={14} />
                <span>{t('sidebar.newFolder')}</span>
              </button>
            )}
          </div>
        </>
      )}

      {/* The SAME shared ConfirmModal the single-item path uses. `window.confirm`
          THROWS in the packaged app (dialog:default has no allow-confirm), so it
          is never an option here. The bulk message names the COUNT — no per-item
          list, no undo (single-item delete has none either, and two conventions
          would be worse than one). */}
      <ConfirmModal
        isOpen={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm?.kind === 'bulk') {
            handleBulkDeleteConfirm();
          } else if (deleteConfirm) {
            const { type, item } = deleteConfirm;
            if (type === 'request') onDeleteRequest?.(item);
            else if (type === 'folder') onDeleteFolder?.(item.collectionId, item);
            else if (type === 'collection') onDeleteCollection?.(item);
          }
          setDeleteConfirm(null);
        }}
        title={
          deleteConfirm?.kind === 'bulk'
            ? t('sidebar.deleteSelected')
            : deleteConfirm?.type === 'folder'
              ? t('sidebar.deleteFolder')
              : deleteConfirm?.type === 'collection'
                ? t('sidebar.deleteCollection')
                : t('sidebar.deleteRequest')
        }
        message={
          deleteConfirm?.kind === 'bulk'
            ? t('sidebar.deleteSelectedConfirm', { count: selection.total })
            : t(
                deleteConfirm?.type === 'folder'
                  ? 'sidebar.deleteFolderConfirm'
                  : deleteConfirm?.type === 'collection'
                    ? 'sidebar.deleteCollectionConfirm'
                    : 'sidebar.deleteConfirm',
                { name: deleteConfirm?.item?.name || '' }
              )
        }
        confirmText={t('common.delete')}
        type="danger"
      />

      {/* Move to… reuses the destination picker that already exists, folder
          navigation and inline folder creation included. */}
      <CollectionSelectionModal
        isOpen={movePickerOpen}
        onClose={() => setMovePickerOpen(false)}
        collections={collections}
        title={t('sidebar.moveSelected')}
        description={t('sidebar.moveSelectedDescription', { count: selection.total })}
        confirmLabel={t('sidebar.moveSelectedConfirm')}
        allowFolders
        allowCreate={Boolean(onCreateFolderReturningId)}
        onSelectCollection={handleBulkMove}
        /* The picker's inline "new folder" needs the id BACK so it can select
           what it just made — `onCreateFolder` (the tree's own) returns nothing
           and ignores the name, so it is deliberately not wired here. Inline
           "new collection" is not offered either: the sidebar's own
           `onCreateCollection` opens a modal rather than taking a name. */
        onCreateFolder={onCreateFolderReturningId}
      />
    </>
  );
}
